/**
 * 映像スタイルがモーションのときの設定「声 (あり / なし)」
 * (docs/features/brand-cut-motion-graphics-plan.md §10.7.5・未決 C = 畳む)。
 *
 * モーションには「セリフ」(口の動きに合わせた発話) と「ナレーション」の区別が無く、発話はすべて話者付きの「声」になるので、
 * 「ナレーション」「セリフ」の有無の 2 つのトグルを 1 つに畳む。内部の値は従来どおり 2 つ (include_narration /
 * include_dialogue、DB 属性ツリーが有効なら各ツリーの option key) を持ち、両方に同じ意味の値を書く。
 */

export type MotionVoiceToggleValue = 'unspecified' | 'include' | 'exclude';

type BehaviorOption = { value: string; mode: string | null };

/** 1 つのトグルの値 (legacy の include / exclude・ツリーの option key) → あり / なし / 指定なし。 */
export function behaviorValueToVoice(
  value: string | null | undefined,
  treeEnabled: boolean,
  treeOptions: readonly BehaviorOption[],
): MotionVoiceToggleValue {
  if (!value || value === 'unspecified') return 'unspecified';
  if (value === 'exclude') return 'exclude';
  if (value === 'include' || value === 'generate_only') return 'include';
  if (treeEnabled) {
    const opt = treeOptions.find((o) => o.value === value);
    if (!opt) return 'unspecified';
    return opt.mode === 'none' ? 'exclude' : 'include';
  }
  return 'unspecified';
}

/** あり / なし / 指定なし → 1 つのトグルに書く値 (ツリーが有効なら mode が合う option key・無ければ指定なし)。 */
export function voiceToBehaviorValue(
  voice: MotionVoiceToggleValue,
  treeEnabled: boolean,
  treeOptions: readonly BehaviorOption[],
): string {
  if (voice === 'unspecified') return 'unspecified';
  if (!treeEnabled) return voice;
  const mode = voice === 'exclude' ? 'none' : 'include';
  return treeOptions.find((o) => o.mode === mode)?.value ?? 'unspecified';
}

/** 表示する「声」の値: ナレーションの値を優先し、指定なしならセリフの値。 */
export function deriveMotionVoiceToggle(
  narration: { value: string; treeEnabled: boolean; treeOptions: readonly BehaviorOption[] },
  dialogue: { value: string; treeEnabled: boolean; treeOptions: readonly BehaviorOption[] },
): MotionVoiceToggleValue {
  const n = behaviorValueToVoice(narration.value, narration.treeEnabled, narration.treeOptions);
  if (n !== 'unspecified') return n;
  return behaviorValueToVoice(dialogue.value, dialogue.treeEnabled, dialogue.treeOptions);
}

export const MOTION_VOICE_TOGGLE_OPTIONS: ReadonlyArray<{ value: MotionVoiceToggleValue; label: string }> = [
  { value: 'unspecified', label: '指定なし' },
  { value: 'include', label: 'あり' },
  { value: 'exclude', label: 'なし' },
];
